import moment from 'moment'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Button, Radio } from 'semantic-ui-react'
import CustomModal from 'src/components/configuracion/components/customModal'
import { GenerateMultipleUnMatch } from 'src/graphql/conciliacion'
import { SettleConflictDocNomina } from 'src/graphql/documents'
import { RemoveDocumentNomina, RemoveSelection } from 'src/graphql/newNomina'
import { importAlertState } from 'src/redux/actions/alert'
import { setUpdateDocuments } from 'src/redux/actions/documentostributarios'
import {
  importUpdateProvidersData,
  importUpdateNominaData,
} from 'src/redux/actions/proveedores'
import { formatMoney, formatMoneyWithCurrency } from 'src/utils/formatter'
import { getPendingNominaAmount } from 'src/utils/nominaAmounts'
import {
  getImputableSelections,
  NominaImputationMode,
} from '../general/js/forms/reconcile/ModalNominaImputation'
import './ModalSettleConflictNomina.styles.scss'

const ModalSettleConflictNominas = ({
  data,
  setShowModal,
  showModal,
  setShowConflict,
}) => {
  const { t } = useTranslation('documents')
  const dispatch = useDispatch()
  const settleConflictDocNomina = SettleConflictDocNomina()
  const { executeMutation: generateMultipleUnMatch } = GenerateMultipleUnMatch()
  const removeSelection = RemoveSelection()
  const removeDocumentNomina = RemoveDocumentNomina()

  const meta = data?.meta || data
  const original = Number(meta?.originalAmount || data?.amount || 0) || 0
  const remaining = Number(meta?.remaining ?? 0) || 0
  const bankMatched = Math.max(0, original - remaining)
  const nominaUsed = Number(meta?.nominaStatus?.totalUsed || 0) || 0
  const { conflictMatchAmount, resolvedTxMatch } = useMemo(() => {
    const inMatch = meta?.matchs?.inMatch || []
    const txMatches = inMatch.filter(
      (m) => m?.originType === 'TRANSACTION' || m?.type === 'TRANSACTION',
    )
    if (txMatches.length === 0) {
      return { conflictMatchAmount: 0, resolvedTxMatch: null }
    }
    const sorted = [...txMatches].sort((a, b) => {
      const da = new Date(a?.userRelated?.date || 0).getTime()
      const db = new Date(b?.userRelated?.date || 0).getTime()
      return db - da
    })
    const last = sorted[0]
    return {
      conflictMatchAmount:
        Math.abs(Number(last?.localValue ?? last?.amount ?? 0)) || 0,
      resolvedTxMatch: last ?? null,
    }
  }, [meta])

  const isOverAllocated = original > 0 && bankMatched + nominaUsed > original
  const canLink = !isOverAllocated || bankMatched === nominaUsed

  const imputableSelections = useMemo(
    () => getImputableSelections(meta?.nominaStatus),
    [data],
  )
  const [selectedSelectionId, setSelectedSelectionId] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!showModal) return
    setSelectedSelectionId(imputableSelections[0]?.id ?? null)
  }, [showModal, data?.id, imputableSelections])

  const selectedSelection = imputableSelections.find(
    (s) => s.id === selectedSelectionId,
  )
  const isLinkEnabled = selectedSelection != null
  const removableSelection = selectedSelection || imputableSelections[0] || null
  const NOMINA_STATUS_LABELS = {
    SELECTED: 'Seleccionada',
    IN_CREATED_NOMINA: 'Creada',
    IN_INFORMED_NOMINA: 'Informada',
    IN_BANK_NOMINA: 'En banco',
    APPROVED: 'Aprobada',
    REJECTED: 'Rechazada',
  }

  const handleLink = async () => {
    if (!selectedSelection) return
    setLoading(true)
    try {
      const response = await settleConflictDocNomina(
        {
          documentId: data.id,
          isRelated: true,
          selectionIds: [String(selectedSelection.id)],
        },
        { requestPolicy: 'network-only' },
      )
      if (response?.data?.settleConflictDocumentNomina?.status) {
        dispatch(
          importAlertState({
            content: t('settleConflict.linkedOk'),
            state: 'success',
            duration: 4,
          }),
        )
        dispatch(setUpdateDocuments({ value: true }))
        dispatch(importUpdateProvidersData(true))
        if (setShowConflict) setShowConflict(false)
        setShowModal(false)
      } else {
        dispatch(
          importAlertState({
            content:
              response?.data?.settleConflictDocumentNomina?.message ||
              'Error al vincular.',
            state: 'error',
            duration: 5,
          }),
        )
      }
    } finally {
      setLoading(false)
    }
  }

  const handleIndependent = async () => {
    setLoading(true)
    try {
      const response = await settleConflictDocNomina(
        {
          documentId: data.id,
          isRelated: false,
        },
        { requestPolicy: 'network-only' },
      )
      if (response?.data?.settleConflictDocumentNomina?.status) {
        dispatch(
          importAlertState({
            content: 'Conflicto resuelto.',
            state: 'success',
            duration: 4,
          }),
        )
        dispatch(setUpdateDocuments({ value: true }))
        dispatch(importUpdateProvidersData(true))
        if (setShowConflict) setShowConflict(false)
        setShowModal(false)
      } else {
        dispatch(
          importAlertState({
            content:
              response?.data?.settleConflictDocumentNomina?.message || 'Error.',
            state: 'error',
            duration: 5,
          }),
        )
      }
    } finally {
      setLoading(false)
    }
  }

  const handleUndoMatch = async () => {
    const originId = resolvedTxMatch?.id || data?.latestTxOriginId
    const rawMid = resolvedTxMatch?.matchId || data?.latestTxMatchId
    const matchIdStr = rawMid?.$oid
      ? String(rawMid.$oid)
      : rawMid
        ? String(rawMid)
        : null
    if (!originId) {
      dispatch(
        importAlertState({
          content: t('settleConflict.matchDataNotFound'),
          state: 'error',
          duration: 5,
        }),
      )
      return
    }
    setLoading(true)
    try {
      const res = await generateMultipleUnMatch({
        input: [
          {
            originType: 'TRANSACTION',
            originId: String(originId),
            endType: data?.documentType || 'TAX_DOCUMENT',
            endId: String(data?.id || data?._id || ''),
            companyId: String(data?.companyId || ''),
            ...(matchIdStr && { matchId: matchIdStr }),
          },
        ],
      })
      if (res?.error) {
        dispatch(
          importAlertState({
            content: res.error.message || 'Error al deshacer.',
            state: 'error',
            duration: 5,
          }),
        )
      } else {
        dispatch(
          importAlertState({
            content: 'Match deshecho correctamente.',
            state: 'success',
            duration: 4,
          }),
        )
        dispatch(setUpdateDocuments({ value: true }))
        dispatch(importUpdateProvidersData(true))
        setShowModal(false)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleRemoveFromDoc = async () => {
    const target = removableSelection
    if (!target?.id) return
    setLoading(true)
    try {
      const SELECTED_STATUS = 'SELECTED'
      let res
      if (target.status === SELECTED_STATUS) {
        res = await removeSelection({
          input: [
            {
              documentId: String(data?.id || data?._id),
              selectionId: String(target.id),
              reject: true,
            },
          ],
          deleteOnlyCurrentUser: false,
        })
      } else {
        res = await removeDocumentNomina({
          nominaId: String(target.nominaId),
          selectionId: [String(target.id)],
        })
      }
      if (res?.error) {
        dispatch(
          importAlertState({
            content: t('settleConflict.removeError'),
            state: 'error',
            duration: 5,
          }),
        )
      } else {
        dispatch(
          importAlertState({
            content: t('settleConflict.removedOk'),
            state: 'success',
            duration: 4,
          }),
        )
        dispatch(setUpdateDocuments({ value: true }))
        dispatch(importUpdateNominaData(true))
        dispatch(importUpdateProvidersData(true))
        if (setShowConflict) setShowConflict(false)
        setShowModal(false)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <CustomModal
      open={showModal}
      onClose={() => setShowModal(false)}
      title={t('settleConflict.title')}
      classNameModal="settle-conflict-payment-modal"
      classNameTitle="settle-conflict-payment-modal-title !tw-text-center !tw-text-[16px] !tw-font-semibold"
      size="tiny"
      modalStyle={{ width: '720px', maxWidth: '92vw' }}
      confirmLabel={null}
      content={
        <div style={{ padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 16 }}>

          <p style={{ margin: 0, fontSize: 13, color: '#6b7280', lineHeight: 1.45 }}>
            {isOverAllocated
              ? t('settleConflict.overAllocatedHint')
              : t('settleConflict.reconciledHint')}
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            {[
              { label: t('settleConflict.colDocument'), value: original, color: '#1f2937' },
              { label: t('settleConflict.colBankMatched'), value: bankMatched, color: '#1f7a8c' },
              { label: t('settleConflict.colInNomina'), value: nominaUsed,
                color: isOverAllocated ? '#d14343' : '#1f7a8c' },
            ].map(({ label, value, color }) => (
              <div key={label} style={{
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                padding: '10px 12px',
                background: '#fff',
              }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af',
                  textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                  {label}
                </div>
                <div style={{ fontSize: 16, fontWeight: 700, color }}>
                  $ {formatMoney(value)}
                </div>
              </div>
            ))}
          </div>

          {isOverAllocated && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: '#fff7f7', border: '1px solid #f3c5c5',
              borderRadius: 8, padding: '9px 12px',
            }}>
              <span style={{ fontSize: 12, color: '#d14343', fontWeight: 500, lineHeight: 1.4 }}>
                Los montos suman{' '}
                <strong>$ {formatMoney(bankMatched + nominaUsed)}</strong>,
                superando el valor del documento en{' '}
                <strong>$ {formatMoney(bankMatched + nominaUsed - original)}</strong>.
              </span>
            </div>
          )}

          {canLink && imputableSelections.length > 0 && (
            <div style={{
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              overflow: 'hidden',
            }}>
              <div style={{
                background: '#f9fafb',
                borderBottom: '1px solid #e5e7eb',
                padding: '9px 14px',
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <span style={{
                  fontSize: 12, fontWeight: 700, color: '#374151',
                  textTransform: 'uppercase', letterSpacing: '0.04em',
                }}>
                  Vincular a una nómina existente
                </span>
                <span style={{ fontSize: 12, color: '#9ca3af', marginLeft: 4, lineHeight: 1.35 }}>
                  Selecciona la nómina que corresponde a este pago
                </span>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: '32px 1fr 110px 120px',
                gap: 8, padding: '7px 14px',
                fontSize: 10, fontWeight: 700, color: '#9ca3af',
                textTransform: 'uppercase', letterSpacing: '0.05em',
                borderBottom: '1px solid #f3f4f6',
              }}>
                <span />
                <span>{t('settleConflict.colNomina')}</span>
                <span>{t('settleConflict.colStatus')}</span>
                <span style={{ textAlign: 'right' }}>{t('settleConflict.colPending')}</span>
              </div>

              {imputableSelections.map((sel, idx) => (
                <div
                  key={sel.id}
                  onClick={() => setSelectedSelectionId(sel.id)}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '32px 1fr 110px 120px',
                    alignItems: 'center', gap: 8,
                    padding: '9px 14px',
                    cursor: 'pointer',
                    background: selectedSelectionId === sel.id ? '#f7fbf9' : '#fff',
                    borderBottom: idx < imputableSelections.length - 1
                      ? '1px solid #f3f4f6' : 'none',
                    transition: 'background 0.15s',
                  }}
                >
                  <Radio
                    checked={selectedSelectionId === sel.id}
                    onChange={() => setSelectedSelectionId(sel.id)}
                  />
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 500, color: '#1f2937', lineHeight: 1.35 }}>
                      {sel.nominaName || '-'}
                    </div>
                  </div>
                  <div>
                    <span style={{
                      fontSize: 10, fontWeight: 600,
                      padding: '2px 8px', borderRadius: 12,
                      background: '#f3f4f6', color: '#4b5563',
                      textTransform: 'uppercase',
                    }}>
                      {NOMINA_STATUS_LABELS[sel.status] || sel.status || '-'}
                    </span>
                  </div>
                  <div style={{
                    fontSize: 13, fontWeight: 700,
                    color: '#1f7a8c', textAlign: 'right',
                  }}>
                    $ {formatMoney(getPendingNominaAmount(sel))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      }
      customActions={
        <div className="settle-conflict-payment-modal__actions">

          <Button
            onClick={() => setShowModal(false)}
            disabled={loading}
            className="settle-conflict-payment-modal__button settle-conflict-payment-modal__button--secondary"
          >
            Cerrar
          </Button>

          <div className="settle-conflict-payment-modal__actions-group">
            {!isOverAllocated && (
              <Button
                onClick={handleIndependent}
                loading={loading}
                disabled={loading}
                className="settle-conflict-payment-modal__button settle-conflict-payment-modal__button--secondary"
              >
                No corresponde
              </Button>
            )}

            {isOverAllocated && (
              <Button
                onClick={handleRemoveFromDoc}
                loading={loading}
                disabled={loading || !removableSelection}
                className="settle-conflict-payment-modal__button settle-conflict-payment-modal__button--secondary"
              >
                Quitar de nómina
              </Button>
            )}

            {isOverAllocated && (
              <Button
                onClick={handleUndoMatch}
                loading={loading}
                disabled={loading}
                className="settle-conflict-payment-modal__button settle-conflict-payment-modal__button--danger"
              >
                Deshacer match
              </Button>
            )}

            {canLink && (
              <Button
                onClick={handleLink}
                loading={loading}
                disabled={loading || !isLinkEnabled}
                className="settle-conflict-payment-modal__button settle-conflict-payment-modal__button--primary"
              >
                Vincular
              </Button>
            )}
          </div>
        </div>
      }
    />
  )
}

export default ModalSettleConflictNominas
